// src/pages/HearingRepresentativePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import HearingCalendar from '../components/HearingCalendar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { CalendarDays, FileText } from 'lucide-react'; 
import HearingRecordDetails from '../components/HearingRecordDetails';

// Imports required for the Table view
import api from '../services/api';
import RecordsTable from '../components/RecordsTable';
import Pagination from '../components/Pagination';

const HearingRepresentativePage = () => {
  const { user, logout } = useAuth();

  // --- Navigation & View State ---
  const [viewMode, setViewMode] = useState('calendar'); 
  const [viewingRecordId, setViewingRecordId] = useState(null);

  // --- UI State (Added to match Admin logic) ---
  const [message, setMessage] = useState({ text: '', type: '' });

  // --- Table Data & Pagination State ---
  const [records, setRecords] = useState([]);
  const [collectors, setCollectors] = useState([]); // State for collectors
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const [filterCollector, setFilterCollector] = useState(''); 
  const [selectedRecords, setSelectedRecords] = useState([]); 

  const navItems = [
    { label: 'Hearing Calendar', icon: <CalendarDays className="w-4 h-4" />, mode: 'calendar' },
    { label: 'All Patients', icon: <FileText className="w-4 h-4" />, mode: 'records' },
  ];

  // --- Helper: Show Message (Matches Admin Page) ---
  const showMessage = (text, type = 'error', duration = 5000) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), duration);
  };

  // --- 1. Fetch Collectors Logic (Matches Admin Page) ---
  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        const response = await api.get('/users/collectors');
        setCollectors(response.data);
      } catch (err) {
        console.error('Failed to fetch collectors.', err);
        // Added error feedback so you know if the fetch fails (likely 403 Forbidden if not showing)
        showMessage('Could not fetch the list of collectors.'); 
      }
    };
    fetchCollectors();
  }, []);

  // --- Data Fetching Logic ---
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      
      if (filterCollector) params.collectorId = filterCollector;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get('/records', { params });
      setRecords(response.data.data || []);
      setTotalRecords(response.data.total || 0);
      setSelectedRecords([]); 
    } catch (err) {
      console.error('Failed to fetch records', err);
      showMessage('Failed to fetch records.');
      setRecords([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, filterCollector]);

  useEffect(() => {
    if (viewMode === 'records' && !viewingRecordId) {
      fetchRecords();
    }
  }, [fetchRecords, viewMode, viewingRecordId]);

  useEffect(() => {
    setPage(1);
  }, [filterCollector, searchQuery]);

  // --- Handlers ---
  const handleViewDetails = (id) => {
    setViewingRecordId(id);
  };

  const handleRecordSelect = (record) => {
    if (record && record._id) {
      setViewingRecordId(record._id);
    }
  };

  const handleBackToView = () => {
    setViewingRecordId(null);
  };

  const handleSelectionChange = (recordId) => {
    setSelectedRecords(prev => (prev.includes(recordId) ? prev.filter(id => id !== recordId) : [...prev, recordId]));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRecords(records.map(r => r._id));
    } else {
      setSelectedRecords([]);
    }
  };

  // Prepare collectors list for the table (Logic from AdminPage)
  const collectorsForDropdown = [{ _id: 'unassigned', username: 'Unassigned' }, ...collectors];

  return (
    <>
      <Header
        user={user}
        onLogout={logout}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => {
            setViewMode(mode);
            setViewingRecordId(null);
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        
        {/* Message Alert Block (Added to match Admin Page) */}
        {message.text && (
            <div className="w-full px-4 sm:px-6 lg:px-8 pt-4">
                <div
                className={`p-4 rounded-xl border-l-4 ${
                    message.type === 'success'
                    ? 'bg-green-50 border-green-500 text-green-800'
                    : 'bg-red-50 border-red-500 text-red-800'
                }`}
                >
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                    {message.type === 'success' ? (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                        </div>
                    ) : (
                        <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        </div>
                    )}
                    </div>
                    <div className="ml-3">
                    <p className="font-medium">{message.text}</p>
                    </div>
                </div>
                </div>
            </div>
        )}

        {/* 1. DETAILS VIEW */}
        {viewingRecordId ? (
          <HearingRecordDetails
            recordId={viewingRecordId}
            onBack={handleBackToView}
          />
        ) : (
          
          /* 2. MAIN VIEWS SWITCHER */
          <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
            
            {/* VIEW A: CALENDAR */}
            {viewMode === 'calendar' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="rounded-lg overflow-hidden border border-gray-100 shadow-inner bg-white">
                  <HearingCalendar onViewDetails={handleViewDetails} />
                </div>
              </div>
            )}

            {/* VIEW B: RECORDS TABLE */}
            {viewMode === 'records' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <RecordsTable
                  records={records}
                  collectors={collectorsForDropdown} // Passes the combined list
                  filterCollector={filterCollector}
                  setFilterCollector={setFilterCollector}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onRecordSelect={handleRecordSelect}
                  loading={loading}
                  selectedRecords={selectedRecords}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeleteSelected={() => {}} 
                  onReassign={() => {}}
                  userRole={user?.role}
                />

                <Pagination
                  page={page}
                  setPage={setPage}
                  limit={limit}
                  setLimit={setLimit}
                  total={totalRecords}
                />
              </div>
            )}

          </div>
        )}
      </div>
    </>
  );
};

export default HearingRepresentativePage;